'use client'

import { motion, MotionValue, useScroll, useTransform } from 'framer-motion'
import React, { ReactNode, useRef } from 'react'
import clsx from 'clsx'
import { isTextBlock, RichText } from '@/shared/components/interactive/RichText'

type Props = {
  blocks: Array<RichText>
  className?: string
}

export default function ScrollRevealText({ blocks, className }: Props) {
  const totalWords = blocks.reduce((acc, curr) => acc + curr.length(), 0)
  const percentagePerWords = 1 / totalWords

  const target = useRef(null)
  const { scrollYProgress } = useScroll({
    target,
    offset: ['start 0.8', 'end 0.8'],
  })

  // We basically need the total number of words in the beginning to actually construct the motion.span block.
  // Maybe we can construct a list of objects that contains literally everything it needs, besides the total number of
  // words, and iterate again during render and pass in the total length?

  let idx = 0
  const children: Array<ReactNode> = []
  for (const block of blocks) {
    const curr = block.value
    if (isTextBlock(curr)) {
      const {
        content: { value, className, box },
      } = curr

      // If the text block is a boxed one (i.e the one with a background color), then it should be rendered together
      // instead
      if (box) {
        const words = value.split(' ').length
        const start = idx * percentagePerWords
        const end = start + percentagePerWords * words

        children.push(
          <AnimatedText
            key={idx}
            value={scrollYProgress}
            input={[start, end]}
            content={value}
            className={className}
            box={box}
          />,
        )

        idx += words
      } else {
        value.split(' ').forEach((word) => {
          const start = idx * percentagePerWords
          const end = start + percentagePerWords

          children.push(
            <AnimatedText
              key={idx}
              value={scrollYProgress}
              input={[start, end]}
              content={`${word} `}
              className={className}
            />,
          )

          idx++
        })
      }
    } else {
      const start = idx * percentagePerWords
      const end = start + percentagePerWords * curr.wordCount
      children.push(
        <AnimatedText
          key={idx}
          value={scrollYProgress}
          input={[start, end]}
          content={curr.children}
        />,
      )

      idx += curr.wordCount
    }
  }

  return (
    <p className={className} ref={target}>
      {children}
    </p>
  )
}

type AnimatedTextProps = {
  value: MotionValue<number>
  input: [number, number]
  content: ReactNode
  className?: string
  box?: string
}

// TODO: Very specific for this but let's try making this generic
function AnimatedText({
  value,
  input,
  content,
  className,
  box,
}: AnimatedTextProps) {
  const progress = useTransform(value, input, [0.2, 1])
  const width = useTransform(value, input, ['0%', '100%'])
  const filter = useTransform(value, input, ['blur(1px)', 'blur(0px)'])

  if (!box)
    return (
      <motion.span
        style={{ opacity: progress, filter }}
        className={clsx('relative', className)}
      >
        {content}
      </motion.span>
    )

  return (
    <>
      <motion.span
        style={{ filter }}
        className={clsx('relative whitespace-nowrap px-2', className)}
      >
        <motion.span className="relative" style={{ opacity: progress }}>
          {content}
        </motion.span>
        <span className="absolute left-1/2 top-1/2 flex h-[115%] w-[105%] -translate-x-1/2 -translate-y-1/2 justify-start">
          <motion.span
            style={{ width }}
            className={clsx(
              'relative h-full w-full overflow-hidden text-white',
              box,
            )}
          >
            <span className="absolute left-3 top-1/2 -translate-y-1/2">
              {content}
            </span>
          </motion.span>
        </span>
      </motion.span>{' '}
    </>
  )
}
